import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* Use dynamic viewport height for mobile */
  background: rgba(var(--color-background-rgb), 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  box-sizing: border-box;
  animation: ${props => props.$isOpen ? 'fadeIn 0.3s ease-out' : 'none'};
  overflow: visible;
  transition: background 0.3s var(--transition-smooth);

  @media (max-width: 768px) {
    align-items: flex-start;
    padding: 15px;
    padding-top: max(15px, env(safe-area-inset-top));
    padding-bottom: max(15px, env(safe-area-inset-bottom));
  }

  @media (max-width: 480px) {
    padding: 10px;
    padding-top: max(10px, env(safe-area-inset-top));
    padding-bottom: max(10px, env(safe-area-inset-bottom));
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      backdrop-filter: blur(0px);
    }
    to {
      opacity: 1;
      backdrop-filter: blur(8px);
    }
  }
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 40px);
  max-height: calc(100dvh - 40px);
  overflow: visible;
  box-sizing: border-box;
  animation: slideInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin: auto;
  
  &[data-modal-container] {
    /* Used by dropdown to detect modal container */
  }

  @keyframes slideInScale {
    from {
      transform: translateY(-20px) scale(0.95);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    max-width: calc(100vw - 30px);
    width: calc(100vw - 30px);
    max-height: calc(100vh - 30px);
    max-height: calc(100dvh - 30px);
    margin: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  @media (max-width: 480px) {
    max-width: calc(100vw - 20px);
    width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    max-height: calc(100dvh - 20px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Custom scrollbar for modal content */
  &::-webkit-scrollbar {
    width: 6px;
    -webkit-appearance: none;
  }

  /* Hide scrollbar arrows completely */
  &::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    -webkit-appearance: none;
  }

  &::-webkit-scrollbar-button:start:decrement,
  &::-webkit-scrollbar-button:end:increment {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  &::-webkit-scrollbar-button:vertical:start:decrement,
  &::-webkit-scrollbar-button:vertical:end:increment {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(204, 51, 51, 0.6);
    border-radius: 999px; /* Pill shape - fully rounded ends */
    min-height: 40px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(204, 51, 51, 0.8);
  }
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = React.memo(({ isOpen, onClose, children }) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const previousActiveElement = React.useRef<HTMLElement | null>(null);

  // Prevent body scroll when modal is open on mobile only
  React.useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement as HTMLElement;
      
      // Only prevent scroll on mobile devices
      if (window.innerWidth <= 768) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = '0';
      }
      
      // Focus trap and focus management
      if (modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        firstElement?.focus();
      }
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      
      // Restore focus to previously focused element
      previousActiveElement.current?.focus();
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const handleOverlayClick = React.useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay 
      $isOpen={isOpen} 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <ModalContainer 
        ref={modalRef}
        data-modal-container
        onKeyDown={handleKeyDown}
        role="document"
        tabIndex={-1}
      >
        {children}
      </ModalContainer>
    </ModalOverlay>
  );
});

Modal.displayName = 'Modal';
